import React, { useState, useEffect } from 'react';
import { Lock, Check } from 'lucide-react';
import styles from './form.module.css';
import ParticipantQuestionImage from './ParticipantQuestionImage';

function selectionEquals(a, b, isMulti) {
    if (isMulti) {
        const setA = new Set(Array.isArray(a) ? a : (a != null ? [a] : []));
        const setB = new Set(Array.isArray(b) ? b : (b != null ? [b] : []));
        if (setA.size !== setB.size) return false;
        for (const x of setA) if (!setB.has(x)) return false;
        return true;
    }
    return a === b;
}

const MCForm = ({ content, onSubmit, isPreview = false, isLocked = false, userAnswer = null, allowUpdateResponse = true }) => {
    const options = content.options || [];
    const isMulti = content.settings?.multipleOptions;
    const maxSelect = Math.max(1, Number(content.settings?.maxSelect) || 2);

    const initialFromUserAnswer = () => {
        if (!userAnswer || userAnswer.selected === undefined) return isMulti ? new Set() : null;
        const sel = userAnswer.selected;
        if (isMulti) return new Set(Array.isArray(sel) ? sel : (sel != null ? [sel] : []));
        return typeof sel === 'number' ? sel : null;
    };
    const [selected, setSelected] = useState(initialFromUserAnswer);

    useEffect(() => {
        if (!userAnswer || userAnswer.selected === undefined) {
            setSelected(isMulti ? new Set() : null);
            return;
        }
        const sel = userAnswer.selected;
        if (isMulti) setSelected(new Set(Array.isArray(sel) ? sel : (sel != null ? [sel] : [])));
        else setSelected(typeof sel === 'number' ? sel : null);
    }, [userAnswer, isMulti]);

    const toggle = (i) => {
        if (optionsDisabled) return;
        if (isMulti) {
            setSelected(prev => {
                const next = new Set(prev);
                if (next.has(i)) next.delete(i);
                else if (next.size < maxSelect) next.add(i);
                return next;
            });
        } else {
            setSelected(i);
        }
    };

    const handleSubmit = () => {
        if (isLocked) return;
        const answer = isMulti ? { selected: [...selected] } : { selected };
        onSubmit(answer);
    };

    const isSelected = (i) => isMulti ? selected.has(i) : selected === i;
    const hasSubmitted = userAnswer != null;
    const optionsDisabled = isLocked || (hasSubmitted && !allowUpdateResponse);
    const currentSelection = isMulti ? [...selected] : selected;
    const isUnchanged = hasSubmitted && selectionEquals(currentSelection, userAnswer?.selected, isMulti);
    const canSubmit = isMulti ? selected.size > 0 : selected !== null;
    const submitLabel = !hasSubmitted
        ? (isPreview ? 'Submit' : isLocked ? 'Voting Closed' : 'Submit')
        : !allowUpdateResponse || isUnchanged
            ? <>Submitted <Check size={14} strokeWidth={3} aria-hidden /></>
            : 'Update response';
    const submitDisabled = isPreview || isLocked || !canSubmit || (hasSubmitted && (!allowUpdateResponse || isUnchanged));

    return (
        <div className={styles.form}>
            <div className={styles.question}>
                {content.question || 'Choose an option'}
                {isLocked && <span className={styles.lockBadge}><Lock size={12} aria-hidden /> Voting Locked</span>}
            </div>
            {isMulti && (
                <div className={styles.description} style={{ marginTop: 4, marginBottom: 0 }}>
                    Select up to {maxSelect} option{maxSelect !== 1 ? 's' : ''}.
                </div>
            )}

            {content.settings?.description && content.settings?.descText && (
                <div className={styles.description}>
                    {content.settings.descText}
                </div>
            )}

            {content.settings?.imageUrl && (
                <ParticipantQuestionImage imageUrl={content.settings.imageUrl} alt="Question visual" />
            )}

            {options.map((opt, i) => (
                <button
                    key={i}
                    className={`${styles.option} ${isSelected(i) ? styles.selected : ''}`}
                    onClick={() => toggle(i)}
                    disabled={optionsDisabled}
                    aria-pressed={isSelected(i)}
                >
                    <span className={`${styles.indicator} ${isMulti ? styles.checkbox : styles.radio} ${isSelected(i) ? styles.checked : ''}`}>
                        {isSelected(i) && <Check size={12} strokeWidth={3} aria-hidden />}
                    </span>
                    {opt || `Option ${i + 1}`}
                </button>
            ))}
            <button
                className={styles.submitBtn}
                onClick={handleSubmit}
                disabled={submitDisabled}
            >
                {submitLabel}
            </button>
        </div>
    );
};

export default MCForm;
